import React, { FC, FormEvent, Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { FiChevronDown, FiLoader, FiSend } from 'react-icons/fi';
import { roleTranslatorReverse } from '../ManageAccounts.component';
import TokenService from '../../../../../services/token-service';
import FastAPIInstance from '../../../../../services/risksystem3-public-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ExternalRoleLabel } from '../../../../../utilities/userRoles';

const roleOptions: ExternalRoleLabel[] = ['Administrator', 'Individual'];

const NewUser: FC = () => {
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<ExternalRoleLabel>('Individual');
  const queryClient = useQueryClient();
  const userDetails = TokenService.getUser();

  // Fetch the default legacy config for the client
  const { data: configName, isLoading: configLoading } = useQuery({
    queryKey: ['default-legacy-config', userDetails?.user_client_id],
    queryFn: async () => {
      if (!userDetails?.user_client_id) {
        throw new Error('Client ID not available');
      }

      const response = await FastAPIInstance.get(
        `/v1/enums/default_legacy_config/${userDetails.user_client_id}`,
        {
          headers: {
            Authorization: 'Bearer ' + TokenService.getLocalAccessToken(),
          },
        },
      );

      return response.data;
    },
    enabled: !!userDetails?.user_client_id,
  });

  // Create a mutation function using React Query
  const sendInviteMutation = useMutation({
    mutationFn: async () => {
      if (!email || email.length === 0) {
        throw new Error('Please enter an email address.');
      }

      if (!configName) {
        throw new Error(
          'No default legacy config found. Please contact RiskSystem support.',
        );
      }

      return FastAPIInstance.post(
        '/v1/external_user_management/create_user_invitation',
        {
          user_email: email,
          user_type: roleTranslatorReverse(role),
          allow_all_funds: true,
          config_name: configName,
        },
        {
          headers: {
            Authorization: 'Bearer ' + TokenService.getLocalAccessToken(),
          },
        },
      );
    },
    onSuccess: () => {
      // Clear the form
      setEmail('');

      // Invalidate the users query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Helper function to extract error message from API response
  const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error) && error.response?.data) {
      // Try to get the error detail from the response
      if (error.response.data.detail) {
        return error.response.data.detail;
      }

      // If there's no detail field but there's some other message
      if (typeof error.response.data === 'string') {
        return error.response.data;
      }
    }

    // If it's a regular Error object
    if (error instanceof Error) {
      return error.message;
    }

    // Fallback error message
    return 'An error occurred. Please try again.';
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    sendInviteMutation.mutate();
  };

  // Show loading state while fetching config
  if (configLoading) {
    return (
      <div className="flex w-full flex-col rounded bg-white p-2.5">
        <div className="w-fit border-b border-mainblue text-xl font-semibold text-mainblue">
          Invite New User
        </div>
        <div className="mt-4 flex items-center justify-center">
          <FiLoader className="h-6 w-6 animate-spin text-mainblue" />
          <span className="ml-2 text-sm text-mainblue">
            Loading configuration...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col rounded bg-white p-2.5">
      <div className="w-fit border-b border-mainblue text-xl font-semibold text-mainblue">
        Invite New User
      </div>
      <div className="mt-1 text-xs font-normal text-mainblue">
        An email will be sent to the new user with a link to create an account.
      </div>
      <div className="text-xs font-normal text-red-600">
        This new user will be given access to all features and funds. If you
        would like to create an account with restricted access, please contact
        RiskSystem support.
      </div>
      <form onSubmit={handleSubmit} autoComplete="off">
        <input
          className="h-7.5 my-2.5 w-72 rounded border border-mainblue px-2.5 py-1 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-mainblue"
          type="email"
          id={`email-input`}
          name="email-input"
          placeholder={'Email Address'}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          disabled={sendInviteMutation.isPending}
        />

        {/* Simplified Headless UI Listbox (Dropdown) with more minimalist selected state */}
        <div className="mb-2.5 w-72">
          <Listbox
            value={role}
            onChange={setRole}
            disabled={sendInviteMutation.isPending}
          >
            <div className="relative">
              <Listbox.Button
                className={`h-7.5 relative w-full cursor-pointer rounded border border-mainblue bg-white px-2.5 py-1 text-left text-sm font-normal focus:outline-none focus:ring-2 focus:ring-mainblue ${sendInviteMutation.isPending ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <span className="block truncate">{role}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <FiChevronDown
                    className="h-4 w-4 text-mainblue"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 w-full overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-md focus:outline-none">
                  {roleOptions.map((option, optionIdx) => (
                    <Listbox.Option
                      key={optionIdx}
                      className={({ active, selected }) => {
                        return ` ${active ? 'bg-blue-50' : ''} ${selected ? 'font-medium text-mainblue' : 'text-gray-900'} cursor-pointer select-none px-4 py-2`;
                      }}
                      value={option}
                    >
                      {option}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        <div className="flex flex-row items-center gap-3">
          <button
            type="submit"
            className={`flex items-center justify-center gap-2 rounded bg-mainblue px-4 py-2 text-sm font-medium text-white transition-all ${
              sendInviteMutation.isPending
                ? 'cursor-not-allowed opacity-70'
                : 'hover:bg-blue-600 active:bg-blue-700'
            }`}
            disabled={sendInviteMutation.isPending}
          >
            {sendInviteMutation.isPending ? (
              <>
                <FiLoader className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <FiSend className="h-4 w-4" />
                Send Invite
              </>
            )}
          </button>

          {/* Status messages */}
          {sendInviteMutation.isSuccess && (
            <span className="text-sm text-green-600">
              Signup invitation sent successfully!
            </span>
          )}

          {sendInviteMutation.isError && (
            <span className="text-sm text-red-600">
              {getErrorMessage(sendInviteMutation.error)}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewUser;
